import { useRef, useState, useLayoutEffect, useMemo, Fragment, useCallback, memo } from 'react';
import { cloneDeep, get, isArray, isEqual, isPlainObject, isString, merge } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Stack, Box, Collapse, IconButton } from '@mui/material';
import {
  DragIndicator as DragHandleIcon,
  VisibilityOffOutlined as HiddenIcon,
  FlakyOutlined as ConditionIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import {
  hash,
  TextBadge,
  NodePortal,
  NodePortalProvider,
  useEditorStore,
  useFlowBuilder2Store,
  getLayoutComponentLabel,
  LayoutComponentMap,
  LayoutComponentNames,
  mutateLayoutComponentLabel,
  IDataGridProperties,
  ILayoutComponentProps,
  useLayoutComponentSettings,
  useLayoutComponentPropertyEditorMap,
  useDebouncedExecMapper,
  useFlowBuilder2Context,
  WorkUpdateOperation,
  getValueByPath,
  setValueByPath,
  IFlowBuilder2FieldState,
  LayoutComponentPropertyEditorNames,
  useAssistantLoadingStore,
} from '@diana/core';
import { IComponentBuilderProps } from './ComponentBuilder.types';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';
import { FLOW_BUILDER2_PAGE_NS } from '../../../i18n';
import {
  FieldTypeComponent,
  IDndLayoutComponentLocation,
  IDndLayoutComponentDropResult,
  useDragLayoutComponent,
  useDropLayoutComponent,
} from '../../../../../providers/DndLayoutComponents2';
import { useDndComponentHoverEvent } from './useDndComponentHoverEvent';
import { useDndComponentDragEndEvent } from './useDndComponentDragEndEvent';
import { NodePortalWrapper, StyledFieldComponentWrapper } from './ComponentBuilder.styles';
import { useOpenComponent } from '../../../Support/useOpenComponent';
import { DropHereBox } from '../../../Components/DropHereBox/DropHereBox';
import { ValidationTip } from '../../../Components/ValidationTip/ValidationTip';
import { useDeleteComponent } from '../../../Support/useDeleteComponent';
import produce from 'immer';
import { useDndComponentDropEvent } from './useDndComponentDropEvent';
import { removeMemoFromReactComponent } from '../Support';

export const FieldComponentBuilder = memo((props: IComponentBuilderProps) => {
  const {
    layoutId,
    parentId,
    componentId,
    componentName,
    componentSize,
    componentIndex,
    taskId,
    stepId,
    flowId,
  } = props;

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { getSettings } = useLayoutComponentSettings();

  const [isHovered, setIsHovered] = useState<boolean>(false);

  const bodyRef = useRef<HTMLElement>(null);
  const handleRef = useRef<HTMLElement>(null);

  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);
  const isDropPlaceholderActive = useFlowBuilder2PageStore((s) => !!s.layoutDndPlaceholderLocation);
  const isDraggingSomething = useFlowBuilder2PageStore((s) => !!s.layoutDndDragItem);
  const isActive = useFlowBuilder2PageStore((s) => s.activeComponent?.componentId === componentId);

  const getLayoutById = useEditorStore((s) => s.getLayoutById);
  const component = useEditorStore(
    (s) => s.getLayoutById(layoutId)?.components[componentIndex],
    isEqual,
  );

  const hasCondition = !!component && !!get(component, 'condition');
  const isInvisible = !!component?.properties.hidden;
  const isSupported = !!component && !!getSettings(component);
  const isLocked = !!component && get(component, 'source') === 'yaml';

  const { deleteComponent } = useDeleteComponent();

  const validation = useValidation(props);
  const openComponent = useOpenComponent();

  const canEdit = !isLocked && isSupported;
  const disabled = isChatLoading || hasAnyPendingStatus;

  const openComponentEditor = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      if (canEdit && !isActive) {
        setIsHovered(false);
        openComponent({ layoutId, componentId });
      }
    },
    [canEdit, openComponent, isActive, layoutId, componentId],
  );

  const { onDndComponentDragEnd } = useDndComponentDragEndEvent();
  const { onDndComponentHover } = useDndComponentHoverEvent({ layoutId, taskId, stepId, flowId });
  const { onDndComponentDrop } = useDndComponentDropEvent({ layoutId, taskId, stepId, flowId });

  const dndLocation = useMemo<IDndLayoutComponentLocation>(() => {
    return { layoutId, parentId, index: componentIndex };
  }, [componentIndex, layoutId, parentId]);

  const dndDropResult = useMemo<IDndLayoutComponentDropResult>(() => {
    return { componentId, componentSize, location: dndLocation };
  }, [componentId, componentSize, dndLocation]);

  const [{ isDragging }, drag, preview] = useDragLayoutComponent<{ isDragging: boolean }>(
    {
      type: FieldTypeComponent,
      item: () => {
        setIsHovered(false);
        const layout = getLayoutById(layoutId)!;
        const layoutComponent = layout.components[dndLocation.index];

        return {
          origin: dndLocation,
          location: dndLocation,
          component: layoutComponent,
          descendants: [],
          originalComponent: layoutComponent,
        };
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (dragObject, monitor) => {
        onDndComponentDragEnd({ dragObject, monitor });
      },
      isDragging: (monitor) => {
        const { component, location } = monitor.getItem();
        return location?.layoutId === layoutId && component.id === componentId;
      },
    },
    [dndLocation, layoutId, componentId, getLayoutById, onDndComponentDragEnd],
  );

  const [{ isOver }, drop] = useDropLayoutComponent<{ isOver: boolean }>(
    {
      accept: FieldTypeComponent,
      collect: (monitor) => ({ isOver: monitor.isOver() }),
      hover: (dragObject, monitor) => {
        const isScrollingDuringDnd = useFlowBuilder2PageStore.getState().isScrollingDndDragItem;
        if (isScrollingDuringDnd) return;
        onDndComponentHover({
          monitor,
          dragObject,
          targetElement: bodyRef.current,
          targetDropResult: dndDropResult,
        });
      },
      drop: (dragObject, monitor) => {
        const isScrollingDuringDnd = useFlowBuilder2PageStore.getState().isScrollingDndDragItem;
        if (isScrollingDuringDnd) return;
        return onDndComponentDrop({
          dragObject,
          monitor,
          targetElement: bodyRef.current,
          targetDropResult: dndDropResult,
        });
      },
    },
    [dndDropResult, onDndComponentHover, onDndComponentDrop],
  );

  drag(handleRef);
  preview(bodyRef);
  drop(bodyRef);

  useLayoutEffect(() => {
    if (!isActive) setIsHovered(false);
  }, [isActive]);

  return (
    <Box
      ref={bodyRef}
      onMouseEnter={() => !isDraggingSomething && !isActive && setIsHovered(true)}
      onMouseLeave={() => !isActive && setIsHovered(false)}
      data-layout-component-id={componentId}
      data-layout-component={componentName}
    >
      <StyledFieldComponentWrapper
        onClick={openComponentEditor}
        ref={bodyRef}
        active={isActive}
        hovered={isHovered}
        invisible={isInvisible}
        canEdit={canEdit}
      >
        <NodePortalProvider>
          <Stack direction="row" sx={{ opacity: isDragging ? 0 : 1 }}>
            <NodePortalWrapper>
              <NodePortal name="DRAG_HANDLE">
                <Collapse
                  in={(isActive || isHovered) && !disabled}
                  timeout={150}
                  orientation="horizontal"
                >
                  <Box className="flow-builder-element" onClick={(e) => e.stopPropagation()}>
                    <Box
                      ref={handleRef}
                      sx={{ cursor: 'grab', pointerEvents: 'auto !important', lineHeight: 0 }}
                    >
                      <DragHandleIcon sx={{ width: 20, height: 20 }} />
                    </Box>
                  </Box>
                </Collapse>
              </NodePortal>
            </NodePortalWrapper>
            <Box flex={1} sx={{ width: '100%' }}>
              <ComponentContent {...props} />
            </Box>
            <NodePortalWrapper>
              <NodePortal name="ACTION_BUTTONS">
                <Stack
                  className="flow-builder-element"
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                >
                  {!!validation && (
                    <Box p={0.25}>
                      <ValidationTip
                        type="error"
                        message={t('descriptions.notAllRequiredFieldsAreFilled')}
                        fullWidth={componentSize >= 12}
                      />
                    </Box>
                  )}

                  {hasCondition && (
                    <Box p={0.25} lineHeight={0} title={t('labels.conditionApplied')}>
                      <ConditionIcon fontSize="small" />
                    </Box>
                  )}

                  {isInvisible && (
                    <Box p={0.25} lineHeight={0} title={t('labels.hidden')}>
                      <HiddenIcon fontSize="small" />
                    </Box>
                  )}

                  {!isSupported && (
                    <Box p={0.25}>
                      <TextBadge color="info">{t('titles.unsupportedComponent')}</TextBadge>
                    </Box>
                  )}

                  {isLocked && (
                    <Box p={0.25}>
                      <TextBadge color="info">{t('titles.editYamlOnly')}</TextBadge>
                    </Box>
                  )}

                  <Collapse
                    in={(isActive || isHovered) && !disabled}
                    orientation="horizontal"
                    timeout={150}
                  >
                    <IconButton
                      size="small"
                      title="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteComponent({ layoutId, componentId }).catch();
                      }}
                      sx={{ pointerEvents: 'auto !important', p: 0.25 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Collapse>
                </Stack>
              </NodePortal>
            </NodePortalWrapper>
          </Stack>
        </NodePortalProvider>

        {isDragging && !isDropPlaceholderActive && (
          <DropHereBox
            isOver={isOver}
            sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
          />
        )}
      </StyledFieldComponentWrapper>
    </Box>
  );
}, isEqual);

export const useValidation = (props: IComponentBuilderProps) => {
  const { layoutId, componentId, componentIndex } = props;

  const { getSettings } = useLayoutComponentSettings();
  const propertyEditorMap = useLayoutComponentPropertyEditorMap();

  const component = useEditorStore(
    (s) => s.getLayoutById(layoutId)?.components[componentIndex],
    isEqual,
  );
  const flowId = useEditorStore((s) => s.flow?.id ?? '');

  const fieldStatesForComponent = useFlowBuilder2Store(
    (s) => s.fieldStatesMap.layoutComponent?.[componentId] ?? {},
    isEqual,
  );

  const editors = useMemo(() => {
    if (component) {
      const settings = getSettings(component);
      return settings?.editors ?? [];
    }

    return [];
  }, [getSettings, component]);

  const fieldStates = useMemo(() => {
    const result: Partial<Record<LayoutComponentPropertyEditorNames, IFlowBuilder2FieldState>> = {};

    for (const { name } of editors) {
      const fieldState = fieldStatesForComponent?.[name];
      if (fieldState) {
        result[name] = fieldState;
      }
    }

    return result;
  }, [editors, fieldStatesForComponent]);

  const validationResult = useMemo(() => {
    if (component) {
      for (const { name, options = {} } of editors) {
        const fieldState = fieldStates[name];
        if (fieldState?.dirty || fieldState?.touched) {
          const { validate } = propertyEditorMap[name];
          const error = validate({
            component,
            flowId,
            layoutId,
            componentIndex,
            options,
          });
          if (error) return error;
        }
      }
    }

    return null;
  }, [componentIndex, editors, fieldStates, flowId, layoutId, propertyEditorMap, component]);

  const [value, setValue] = useState(validationResult);
  const validationResultRef = useRef(validationResult);

  useLayoutEffect(() => {
    if (validationResult && !validationResultRef.current) {
      validationResultRef.current = validationResult;
      const timeoutId = setTimeout(() => setValue(validationResult), 750);
      return () => clearTimeout(timeoutId);
    }

    validationResultRef.current = validationResult;
    setValue(validationResult);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash(validationResult)]);

  return value;
};

export const ComponentContent = (props: IComponentBuilderProps) => {
  const { componentName, layoutId, componentIndex } = props;

  const { getSettings } = useLayoutComponentSettings();

  const { updateAndSaveLayoutComponentByIndex, updateAndSaveDefaultData } =
    useFlowBuilder2Context();

  const debouncedExec = useDebouncedExecMapper();

  const component = useEditorStore((s) => {
    const layout = s.getLayoutById(layoutId);
    return layout?.components[componentIndex] ?? null;
  }, isEqual);

  const data = useEditorStore((s) => s.getFlowDefaultData(), isEqual);

  const ReactComponent = useMemo(
    () =>
      LayoutComponentMap[componentName] ??
      LayoutComponentMap[LayoutComponentNames.MISSING_COMPONENT],
    [componentName],
  );

  const ReactNoMemoComponent = useMemo(
    () => removeMemoFromReactComponent(ReactComponent),
    [ReactComponent],
  );

  const componentProps = useMemo(() => {
    if (!component) return null;

    const settings = component ? getSettings(component) : null;

    const sanitizedComponent = produce(component, (draftLC) => {
      const label = getLayoutComponentLabel(draftLC);
      const defaultLabel = settings?.displayName ?? component.component;
      draftLC.properties.hidden = false;
      if (!label) {
        mutateLayoutComponentLabel(draftLC, defaultLabel);
        draftLC.properties.label = '';
      }
      if (component.component === LayoutComponentNames.DATA_GRID) {
        (draftLC.properties as IDataGridProperties).disableColumnsOrderComparison = true;
      }
    });

    const transformedComponent =
      settings?.transformPreview?.(sanitizedComponent) ?? sanitizedComponent;

    const lcProps: ILayoutComponentProps = {
      workId: '',
      step: {} as any,
      layoutComponent: cloneDeep(transformedComponent),
      data,
      index: componentIndex,
      isWorkEditable: false,
      isWorkflowEditMode: true,
      disableValidation: true,
      disableAutoUpdate: true,
      eventHandlers: {
        dataGridColumnWidthChange: (params) => {
          const debounceId = [component.id, 'column_width', params.colDef.field].join('.');
          debouncedExec(
            debounceId,
            () => {
              updateAndSaveLayoutComponentByIndex(layoutId, componentIndex, (draft) => {
                draft.properties.columns?.forEach((column) => {
                  if (column.name === params.colDef.field) column.columnWidth = params.width;
                });
                return draft;
              });
            },
            500,
          );
        },
      },
    };

    // check if default data is configurable
    if (component.properties.withCustomData) {
      lcProps.isWorkEditable = true;
      lcProps.isWorkflowEditMode = false;
      lcProps.onUpdate = ({ value, options }) => {
        const path = options?.dataPath ?? component.dataPath ?? '';
        if (!path.startsWith('$')) return;
        updateAndSaveDefaultData((json) => {
          let draft = cloneDeep(getValueByPath({ json, path }));
          if (options?.operation === WorkUpdateOperation.Push) {
            draft = draft ?? [];
            if (isArray(draft)) draft.push(value);
          } else if (options?.operation === WorkUpdateOperation.Unset) {
            draft = undefined;
          } else if (options?.operation === WorkUpdateOperation.Merge) {
            if (isArray(value)) {
              draft = draft ?? [];
              if (isArray(draft)) merge(draft, value);
            }
            if (isPlainObject(value)) {
              draft = draft ?? {};
              if (isPlainObject(draft)) merge(draft, value);
            }
          } else {
            draft = value;
          }

          if (isString(draft) && !draft) draft = undefined;

          return setValueByPath({ json, path, value: draft });
        });
      };
    }

    return lcProps;
  }, [
    data,
    component,
    debouncedExec,
    getSettings,
    componentIndex,
    layoutId,
    updateAndSaveDefaultData,
    updateAndSaveLayoutComponentByIndex,
  ]);

  return componentProps ? <ReactNoMemoComponent {...componentProps} /> : <Fragment />;
};
